import { AbstractControl, ValidatorFn } from '@angular/forms';
import { PhoneNumber } from '@fmnts/common/phone';
import { hasProperty, isDefined } from '@fmnts/core';
import * as O from 'effect/Option';
import * as Str from 'effect/String';

export type E164PhoneFormatValidationError = 'subscriber-pattern';
export type PhoneNumberStartsWithZeroValidationError = 'starts-with-zero';

/**
 * Type guard for checking a "e164PhoneFormat" validation error
 *
 * @param e Errors object
 *
 * @returns
 * `true` if the passed errors object has
 * "e164PhoneFormat" validation errors
 */
export const hasE164PhoneFormatError = hasProperty(
  'e164PhoneFormat',
  (u): u is E164PhoneFormatValidationError => isDefined(u),
);

/**
 * Type guard for checking a "starts-with-zero" validation error
 *
 * @param e Errors object
 *
 * @returns
 * `true` if the passed errors object has
 * "startsWithZero" validation errors
 */
export const phoneNumberStartsWithZeroError = hasProperty(
  'startsWithZero',
  (u): u is PhoneNumberStartsWithZeroValidationError => isDefined(u),
);

/**
 * @returns
 * Validator function for validating phone formats
 */
export function e164PhoneFormat(): ValidatorFn {
  return (control: AbstractControl) => {
    const value: unknown = control.value;
    if (!Str.isString(value) || Str.isEmpty(value)) {
      return null;
    }

    const unvalidated = PhoneNumber.parse(value);
    if (!PhoneNumber.hasPhoneNumber(unvalidated)) {
      return null;
    }

    if (unvalidated.phoneNumber.startsWith('0')) {
      return { startsWithZero: 'starts-with-zero' };
    }

    if (!/^\d+$/.test(unvalidated.phoneNumber)) {
      return { e164PhoneFormat: 'subscriber-pattern' };
    }
    /**
     * The maximum length of a phone number is 15 characters.
     * This is the maximum length of a phone number in the E.164 format.
     * https://en.wikipedia.org/wiki/E.164
     */
    const countryCode = unvalidated.countryCode.pipe(O.getOrElse(() => ''));
    const length = `${countryCode}${unvalidated.phoneNumber}`.length;
    if (length > 15) {
      // create same error that the Validators.maxLength would return
      return { maxlength: { requiredLength: 15, actualLength: length } };
    }

    return null;
  };
}
